import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { 
  Users, 
  MousePointer, 
  Globe, 
  Smartphone, 
  TrendingUp, 
  Clock,
  MapPin,
  ChevronRight,
  LogOut,
  BarChart3,
  Eye
} from "lucide-react";

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Login Component
const AdminLogin = ({ onLogin }) => {
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");

    try {
      const res = await fetch(`${API_URL}/api/admin/login`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ password })
      });

      if (res.ok) {
        const data = await res.json();
        localStorage.setItem("admin_token", data.token);
        onLogin(data.token);
      } else {
        setError("Invalid password");
      }
    } catch (err) {
      setError("Connection error");
    }
    setLoading(false);
  };

  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-xl p-8 w-full max-w-md">
        <h1 className="text-2xl font-semibold text-center mb-2">Admin Dashboard</h1>
        <p className="text-gray-500 text-center mb-6">Digital Prints Club Analytics</p>
        
        <form onSubmit={handleSubmit}>
          <input
            type="password"
            placeholder="Enter admin password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            className="w-full px-4 py-3 border border-gray-200 rounded-xl mb-4 focus:outline-none focus:ring-2 focus:ring-gray-900"
            data-testid="admin-password-input"
          />
          {error && <p className="text-red-500 text-sm mb-4">{error}</p>}
          <button
            type="submit"
            disabled={loading}
            className="w-full bg-gray-900 text-white py-3 rounded-xl font-medium hover:bg-gray-800 transition-colors disabled:opacity-50"
            data-testid="admin-login-button"
          >
            {loading ? "Logging in..." : "Login"}
          </button>
        </form>
      </div>
    </div>
  );
};

// Stat Card Component
const StatCard = ({ icon: Icon, label, value, subValue, color = "gray" }) => (
  <div className="bg-white rounded-xl p-5 shadow-sm border border-gray-100">
    <div className="flex items-center gap-3 mb-3">
      <div className={`p-2 rounded-lg bg-${color}-100`}>
        <Icon className={`w-5 h-5 text-${color}-600`} />
      </div>
      <span className="text-gray-500 text-sm">{label}</span>
    </div>
    <p className="text-3xl font-semibold">{value}</p>
    {subValue && <p className="text-sm text-gray-400 mt-1">{subValue}</p>}
  </div>
);

// Dashboard Component
const Dashboard = ({ token, onLogout }) => {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("overview");

  useEffect(() => {
    fetchStats();
    const interval = setInterval(fetchStats, 30000); // Refresh every 30s
    return () => clearInterval(interval);
  }, [token]);

  const fetchStats = async () => {
    try {
      const res = await fetch(`${API_URL}/api/admin/stats`, {
        headers: { "Authorization": `Bearer ${token}` }
      });

      if (res.ok) {
        const data = await res.json();
        setStats(data);
      } else if (res.status === 401) {
        onLogout();
      }
    } catch (err) {
      console.error("Failed to fetch stats:", err);
    }
    setLoading(false);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-gray-500">Loading stats...</div>
      </div>
    );
  }

  if (!stats) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-gray-500">Failed to load stats</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white border-b border-gray-200 sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-4 py-4 flex items-center justify-between">
          <div>
            <h1 className="text-xl font-semibold">Digital Prints Club</h1>
            <p className="text-sm text-gray-500">Analytics Dashboard</p>
          </div>
          <button
            onClick={onLogout}
            className="flex items-center gap-2 text-gray-500 hover:text-gray-700 transition-colors"
            data-testid="logout-button"
          >
            <LogOut className="w-4 h-4" />
            Logout
          </button>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 py-6">
        {/* Tabs */}
        <div className="flex gap-2 mb-6 overflow-x-auto pb-2">
          {["overview", "locations", "sources", "clicks", "recent"].map((tab) => (
            <button
              key={tab}
              onClick={() => setActiveTab(tab)}
              className={`px-4 py-2 rounded-lg text-sm font-medium whitespace-nowrap transition-colors ${
                activeTab === tab
                  ? "bg-gray-900 text-white"
                  : "bg-white text-gray-600 hover:bg-gray-100"
              }`}
            >
              {tab.charAt(0).toUpperCase() + tab.slice(1)}
            </button>
          ))}
        </div>

        {/* Overview Tab */}
        {activeTab === "overview" && (
          <div className="space-y-6">
            {/* Stats Grid */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <StatCard
                icon={Eye}
                label="Total Visits"
                value={stats.overview.total_visits.toLocaleString()}
                subValue={`${stats.overview.today_visits} today`}
              />
              <StatCard
                icon={Users}
                label="Unique Visitors"
                value={stats.overview.unique_visitors.toLocaleString()}
              />
              <StatCard
                icon={MousePointer}
                label="CTA Clicks"
                value={stats.overview.total_clicks.toLocaleString()}
                subValue={`${stats.overview.today_clicks} today`}
              />
              <StatCard
                icon={TrendingUp}
                label="Conversion Rate"
                value={`${stats.overview.conversion_rate}%`}
                subValue="clicks / visits"
              />
            </div>

            {/* Charts Row */}
            <div className="grid md:grid-cols-2 gap-6">
              {/* Daily Visits Chart */}
              <div className="bg-white rounded-xl p-5 shadow-sm border border-gray-100">
                <h3 className="font-medium mb-4 flex items-center gap-2">
                  <BarChart3 className="w-4 h-4" />
                  Daily Visits (Last 30 Days)
                </h3>
                <div className="h-48 flex items-end gap-1">
                  {stats.daily_visits.length > 0 ? (
                    stats.daily_visits.map((day, i) => {
                      const maxCount = Math.max(...stats.daily_visits.map(d => d.count));
                      const height = (day.count / maxCount) * 100;
                      return (
                        <div
                          key={i}
                          className="flex-1 bg-gray-900 rounded-t hover:bg-gray-700 transition-colors"
                          style={{ height: `${Math.max(height, 2)}%` }}
                          title={`${day.date}: ${day.count} visits`}
                        />
                      );
                    })
                  ) : (
                    <p className="text-gray-400 text-sm w-full text-center">No data yet</p>
                  )}
                </div>
              </div>

              {/* Top Countries */}
              <div className="bg-white rounded-xl p-5 shadow-sm border border-gray-100">
                <h3 className="font-medium mb-4 flex items-center gap-2">
                  <Globe className="w-4 h-4" />
                  Top Countries
                </h3>
                <div className="space-y-3">
                  {stats.by_country.slice(0, 5).map((country, i) => (
                    <div key={i} className="flex items-center justify-between">
                      <span className="text-gray-700">{country.country}</span>
                      <span className="text-gray-500 text-sm">{country.count}</span>
                    </div>
                  ))}
                  {stats.by_country.length === 0 && (
                    <p className="text-gray-400 text-sm">No data yet</p>
                  )}
                </div>
              </div>
            </div>

            {/* More Stats Row */}
            <div className="grid md:grid-cols-3 gap-6">
              {/* Traffic Sources */}
              <div className="bg-white rounded-xl p-5 shadow-sm border border-gray-100">
                <h3 className="font-medium mb-4">Traffic Sources</h3>
                <div className="space-y-3">
                  {stats.by_source.slice(0, 5).map((source, i) => (
                    <div key={i} className="flex items-center justify-between">
                      <span className="text-gray-700">{source.source}</span>
                      <span className="text-gray-500 text-sm">{source.count}</span>
                    </div>
                  ))}
                  {stats.by_source.length === 0 && (
                    <p className="text-gray-400 text-sm">No data yet</p>
                  )}
                </div>
              </div>

              {/* Devices */}
              <div className="bg-white rounded-xl p-5 shadow-sm border border-gray-100">
                <h3 className="font-medium mb-4 flex items-center gap-2">
                  <Smartphone className="w-4 h-4" />
                  Devices
                </h3>
                <div className="space-y-3">
                  {stats.by_device.map((device, i) => (
                    <div key={i} className="flex items-center justify-between">
                      <span className="text-gray-700">{device.device}</span>
                      <span className="text-gray-500 text-sm">{device.count}</span>
                    </div>
                  ))}
                  {stats.by_device.length === 0 && (
                    <p className="text-gray-400 text-sm">No data yet</p>
                  )}
                </div>
              </div>

              {/* Top CTAs */}
              <div className="bg-white rounded-xl p-5 shadow-sm border border-gray-100">
                <h3 className="font-medium mb-4 flex items-center gap-2">
                  <MousePointer className="w-4 h-4" />
                  Top CTAs Clicked
                </h3>
                <div className="space-y-3">
                  {stats.by_button.slice(0, 5).map((btn, i) => (
                    <div key={i} className="flex items-center justify-between">
                      <span className="text-gray-700 text-sm truncate max-w-[150px]">{btn.button}</span>
                      <span className="text-gray-500 text-sm">{btn.count}</span>
                    </div>
                  ))}
                  {stats.by_button.length === 0 && (
                    <p className="text-gray-400 text-sm">No clicks yet</p>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Locations Tab */}
        {activeTab === "locations" && (
          <div className="grid md:grid-cols-2 gap-6">
            <div className="bg-white rounded-xl p-5 shadow-sm border border-gray-100">
              <h3 className="font-medium mb-4">By Country</h3>
              <div className="space-y-3 max-h-[500px] overflow-y-auto">
                {stats.by_country.map((country, i) => (
                  <div key={i} className="flex items-center justify-between py-2 border-b border-gray-50">
                    <span className="text-gray-700">{country.country}</span>
                    <span className="text-gray-500">{country.count}</span>
                  </div>
                ))}
              </div>
            </div>
            <div className="bg-white rounded-xl p-5 shadow-sm border border-gray-100">
              <h3 className="font-medium mb-4">By City</h3>
              <div className="space-y-3 max-h-[500px] overflow-y-auto">
                {stats.by_city.map((city, i) => (
                  <div key={i} className="flex items-center justify-between py-2 border-b border-gray-50">
                    <span className="text-gray-700">{city.city}, {city.country}</span>
                    <span className="text-gray-500">{city.count}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* Sources Tab */}
        {activeTab === "sources" && (
          <div className="grid md:grid-cols-2 gap-6">
            <div className="bg-white rounded-xl p-5 shadow-sm border border-gray-100">
              <h3 className="font-medium mb-4">Traffic Sources (UTM)</h3>
              <div className="space-y-3">
                {stats.by_source.map((source, i) => {
                  const total = stats.by_source.reduce((a, b) => a + b.count, 0);
                  const pct = total > 0 ? ((source.count / total) * 100).toFixed(1) : 0;
                  return (
                    <div key={i}>
                      <div className="flex items-center justify-between mb-1">
                        <span className="text-gray-700">{source.source}</span>
                        <span className="text-gray-500 text-sm">{source.count} ({pct}%)</span>
                      </div>
                      <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
                        <div
                          className="h-full bg-gray-900 rounded-full"
                          style={{ width: `${pct}%` }}
                        />
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
            <div className="bg-white rounded-xl p-5 shadow-sm border border-gray-100">
              <h3 className="font-medium mb-4">Devices</h3>
              <div className="space-y-3">
                {stats.by_device.map((device, i) => {
                  const total = stats.by_device.reduce((a, b) => a + b.count, 0);
                  const pct = total > 0 ? ((device.count / total) * 100).toFixed(1) : 0;
                  return (
                    <div key={i}>
                      <div className="flex items-center justify-between mb-1">
                        <span className="text-gray-700">{device.device}</span>
                        <span className="text-gray-500 text-sm">{device.count} ({pct}%)</span>
                      </div>
                      <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
                        <div
                          className="h-full bg-gray-900 rounded-full"
                          style={{ width: `${pct}%` }}
                        />
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        )}

        {/* Clicks Tab */}
        {activeTab === "clicks" && (
          <div className="bg-white rounded-xl p-5 shadow-sm border border-gray-100">
            <h3 className="font-medium mb-4">CTA Button Clicks</h3>
            <div className="space-y-3">
              {stats.by_button.map((btn, i) => {
                const total = stats.by_button.reduce((a, b) => a + b.count, 0);
                const pct = total > 0 ? ((btn.count / total) * 100).toFixed(1) : 0;
                return (
                  <div key={i}>
                    <div className="flex items-center justify-between mb-1">
                      <span className="text-gray-700">{btn.button}</span>
                      <span className="text-gray-500 text-sm">{btn.count} clicks ({pct}%)</span>
                    </div>
                    <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
                      <div
                        className="h-full bg-green-500 rounded-full"
                        style={{ width: `${pct}%` }}
                      />
                    </div>
                  </div>
                );
              })}
              {stats.by_button.length === 0 && (
                <p className="text-gray-400">No clicks recorded yet</p>
              )}
            </div>
          </div>
        )}

        {/* Recent Tab */}
        {activeTab === "recent" && (
          <div className="grid md:grid-cols-2 gap-6">
            <div className="bg-white rounded-xl p-5 shadow-sm border border-gray-100">
              <h3 className="font-medium mb-4">Recent Visits</h3>
              <div className="space-y-3 max-h-[500px] overflow-y-auto">
                {stats.recent_visits.map((visit, i) => (
                  <div key={i} className="py-2 border-b border-gray-50 text-sm">
                    <div className="flex items-center justify-between">
                      <span className="text-gray-700">{visit.country}, {visit.city}</span>
                      <span className="text-gray-400 text-xs">
                        {new Date(visit.timestamp).toLocaleString()}
                      </span>
                    </div>
                    <div className="text-gray-400 text-xs mt-1">
                      {visit.device} • {visit.utm_source || "Direct"}
                    </div>
                  </div>
                ))}
                {stats.recent_visits.length === 0 && (
                  <p className="text-gray-400">No visits yet</p>
                )}
              </div>
            </div>
            <div className="bg-white rounded-xl p-5 shadow-sm border border-gray-100">
              <h3 className="font-medium mb-4">Recent Clicks</h3>
              <div className="space-y-3 max-h-[500px] overflow-y-auto">
                {stats.recent_clicks.map((click, i) => (
                  <div key={i} className="py-2 border-b border-gray-50 text-sm">
                    <div className="flex items-center justify-between">
                      <span className="text-gray-700 truncate max-w-[200px]">{click.button_id}</span>
                      <span className="text-gray-400 text-xs">
                        {new Date(click.timestamp).toLocaleString()}
                      </span>
                    </div>
                    <div className="text-gray-400 text-xs mt-1">
                      {click.country}
                    </div>
                  </div>
                ))}
                {stats.recent_clicks.length === 0 && (
                  <p className="text-gray-400">No clicks yet</p>
                )}
              </div>
            </div>
          </div>
        )}
      </main>
    </div>
  );
};

// Main Admin Page Component
function AdminPage() {
  const [token, setToken] = useState(localStorage.getItem("admin_token"));
  const navigate = useNavigate();

  const handleLogin = (newToken) => {
    setToken(newToken);
  };

  const handleLogout = () => {
    localStorage.removeItem("admin_token");
    setToken(null);
  };

  if (!token) {
    return <AdminLogin onLogin={handleLogin} />;
  }

  return <Dashboard token={token} onLogout={handleLogout} />;
}

export default AdminPage;
